import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { checkAdminSession, commonLoginV2 } from "../common/api/loginApi";
import { hasAuthenticatedSession } from "../common/utils/sessionContext";
import { resolvePostLoginNavigation, resolveSessionLandingRoute } from "../common/utils/authSession";
import AlertMessage from "../common/components/AlertMessage";
import Backdrop from "@mui/material/Backdrop";
import * as Sentry from "@sentry/react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import "../styles/newLogin.css";

// Accepts either a username or an email address; the server checks the
// admin table first, then family member, and returns whichever matches.

const NewLoginV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [alertStatus, setAlertStatusBase] = useState("");

  const submitDisabled = !identifier.trim().length || !password.length;

  useEffect(() => {
    const prefilledIdentifier = location.state?.email?.trim() || "";
    if (prefilledIdentifier) {
      setIdentifier(prefilledIdentifier.toLowerCase());
    }
  }, [location.state?.email]);

  useEffect(() => {
    // 1. FP / CG session lives in localStorage — treat it as the source of
    //    truth first, so those users never wait on a network round-trip.
    if (hasAuthenticatedSession()) {
      navigate(resolveSessionLandingRoute());
      return;
    }

    // 2. No in-app session: the user may still hold an active admin (JSP)
    //    session in the JSESSIONID cookie. session.do reports it and where to
    //    land (mainMenu.jsp vs community.jsp).
    let active = true;

    (async () => {
      const adminSession = await checkAdminSession();

      if (!active) {
        return;
      }

      if (adminSession.authenticated && adminSession.role === "admin" && adminSession.redirectUrl) {
        window.location.assign(adminSession.redirectUrl);
        return;
      }

      // 3. Nobody is signed in → reveal the React login form.
      setCheckingSession(false);
    })();

    return () => {
      active = false;
    };
  }, [navigate]);

  const showError = (message) => {
    setAlertStatusBase({ message, status: "400", origin: "center" });
  };

  const closeAlert = () => {
    setAlertStatusBase("");
  };

  const handleForgotPassword = () => {
    const trimmedIdentifier = identifier.trim();
    navigate("/forgot-password", { state: { email: trimmedIdentifier } });
  };

  const updateIdentifier = (e) => {
    setIdentifier(e.target.value.replace(/\s/g, "").toLowerCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled || loading) {
      return;
    }

    setLoading(true);

    try {
      const normalizedIdentifier = identifier.trim().toLowerCase();
      const loginRes = await commonLoginV2({ identifier: normalizedIdentifier, password });

      if (!loginRes.success) {
        showError(loginRes.message);
        return;
      }

      localStorage.setItem("userEmail", normalizedIdentifier);

      // Admin: session already established server-side; navigate to the JSP
      // admin app (the one external hand-off). Family continues in-app.
      if (loginRes.role === "admin") {
        window.location.assign(loginRes.redirectUrl);
        return;
      }

      const navigation = await resolvePostLoginNavigation(loginRes, normalizedIdentifier);

      if (!navigation.success) {
        showError(navigation.message);
        return;
      }

      navigate(navigation.path, navigation.state ? { state: navigation.state } : undefined);
    } catch (error) {
      Sentry.captureException(error);
      showError("Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="nl-login-page">
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading || checkingSession}>
        <img src={require("../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}

      <section className="nl-login-brand-pane">
        <div className="nl-login-glow" aria-hidden="true" />
        <div className="nl-login-glow-secondary" aria-hidden="true" />
        <div className="nl-login-brand-inner">
          <h1 className="nl-login-brand-heading">
            BT Homecare
            <br />
            <span className="nl-login-brand-heading-sub">Digital Care Communication</span>
          </h1>
          <p className="nl-login-brand-tagline">Care that connects, communication that grows.</p>
          <p className="nl-login-brand-tagline">Bringing caregivers, families, and care teams together to deliver better outcomes.</p>
        </div>
      </section>

      <section className="nl-login-form-pane">
        <div className="nl-login-card">
          <div className="nl-login-card-header">
            <img className="nl-login-card-logo" src={require("../assets/Loginpage_logo - Copy.png")} alt="BridgeTree Digital Care Communications" />
          </div>

          <h2 className="nl-login-welcome">Welcome back</h2>
          <p className="nl-login-welcome-sub">Sign in to your account.</p>

          <form className="nl-login-form" onSubmit={handleSubmit}>
            <div className="nl-login-field">
              <label htmlFor="nl-login-email" className="nl-login-label">
                Email or Username
              </label>
              <div className="nl-login-input-wrap">
                <FaUser size={14} className="nl-login-input-icon" aria-hidden="true" />
                <input
                  id="nl-login-email"
                  type="text"
                  name="identifier"
                  value={identifier}
                  onChange={updateIdentifier}
                  placeholder="Email or Username"
                  autoComplete="username"
                  maxLength={75}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="nl-login-field">
              <label htmlFor="nl-login-password" className="nl-login-label">
                Password
              </label>
              <div className="nl-login-input-wrap">
                <FaLock size={14} className="nl-login-input-icon" aria-hidden="true" />
                <input
                  id="nl-login-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="nl-login-input--with-action"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  maxLength={75}
                  required
                />
                <button type="button" className="nl-login-toggle-visibility" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            <div className="nl-login-options-row">
              <button type="button" className="nl-login-forgot" onClick={handleForgotPassword}>
                Forgot password?
              </button>
            </div>

            <button type="submit" className="nl-login-submit" disabled={submitDisabled}>
              Sign in
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default NewLoginV2;
